import "@fontsource/ibm-plex-sans/cyrillic-400.css";
import "@fontsource/ibm-plex-sans/cyrillic-500.css";
import "@fontsource/ibm-plex-sans/cyrillic-600.css";
import "@fontsource/ibm-plex-sans-condensed/cyrillic-ext-600.css";
import "@fontsource/ibm-plex-sans-condensed/cyrillic-ext-700.css";
import "./styles.css";

import {
  createConstructorSession,
  type ActionOutcome,
  type AttachmentPointView,
  type BuildRecipe,
  type CatalogModule,
  type ConstructorSession,
  type ConstructorSnapshot,
  type GlobalLivery,
} from "./core/index.ts";
import {
  categoryLabels,
  defaultLivery,
  demonstrationRecipe,
  modulePresentation,
  spikeCatalog,
  type CatalogCategoryId,
  type ModulePresentation,
} from "./catalog/spike-catalog.ts";
import { demoMechs, type DemoMech } from "./catalog/demo-mechs.ts";
import { LocalStorageRecipeStore } from "./adapters/local-storage-recipe-store.ts";
import type { DemonstrationAction } from "./presentation/assembly-surface.ts";
import { ThreeAssemblySurface } from "./presentation/three-assembly-surface.ts";

type AppStatus = "booting" | "ready" | "error" | "disposed";
type PaletteCategory = "compatible" | CatalogCategoryId;
type SuccessfulOutcome = Extract<ActionOutcome, { readonly ok: true }>;
type RejectedOutcome = Extract<ActionOutcome, { readonly ok: false }>;

interface ConstructorBrowserApi {
  ready: boolean;
  status: AppStatus;
  getSnapshot(): ConstructorSnapshot | null;
  debug(): ReturnType<ThreeAssemblySurface["debugSnapshot"]> | null;
  selectPoint(pointId: string): Promise<ActionOutcome | null>;
  previewModule(moduleId: string | null): Promise<ActionOutcome | null>;
  previewBase(moduleId: string): Promise<ActionOutcome | null>;
  previewDemonstrationMech(demonstrationMechId: string): Promise<ActionOutcome | null>;
  play(action: DemonstrationAction): boolean;
  confirmPreview(): Promise<ActionOutcome | null>;
  cancelPreview(): Promise<ActionOutcome | null>;
  dispose(): Promise<void>;
}

declare global {
  interface Window {
    __MECH_CONSTRUCTOR__: ConstructorBrowserApi;
  }
}

const app = requireElement<HTMLElement>("app");
const constructorWorkspace = requireClosest<HTMLElement>(
  requireElement<HTMLElement>("assembly-heading"),
  ".constructor-workspace",
);
const assemblyTree = requireElement<HTMLUListElement>("assembly-tree-list");
const treeLoading = requireElement<HTMLElement>("tree-loading");
const partCount = requireElement<HTMLOutputElement>("part-count");
const catalogDrawer = requireClosest<HTMLElement>(
  requireElement<HTMLElement>("catalog-heading"),
  ".catalog-drawer",
);
const drawerCode = requireElement<HTMLElement>("drawer-code");
const catalogIndex = requireElement<HTMLElement>("catalog-index");
const selectionTitle = requireElement<HTMLElement>("selection-title");
const selectionDetail = requireElement<HTMLElement>("selection-detail");
const selectionRecord = requireClosest<HTMLElement>(selectionTitle, ".selection-record");
const catalogStatus = requireElement<HTMLElement>("catalog-status");
const moduleList = requireElement<HTMLElement>("module-list");
const tryOnPanel = requireElement<HTMLElement>("try-on-panel");
const tryOnSummary = requireElement<HTMLElement>("try-on-summary");
const tryOnConsequences = requireElement<HTMLUListElement>("try-on-consequences");
const confirmButton = requireElement<HTMLButtonElement>("confirm-button");
const cancelButton = requireElement<HTMLButtonElement>("cancel-button");
const undoButton = requireElement<HTMLButtonElement>("undo-button");
const redoButton = requireElement<HTMLButtonElement>("redo-button");
const saveForm = requireElement<HTMLFormElement>("save-form");
const saveName = requireElement<HTMLInputElement>("save-name");
const savedList = requireElement<HTMLUListElement>("saved-list");
const savedCount = requireElement<HTMLElement>("saved-count");
const documentStateLabel = requireElement<HTMLElement>("document-state-label");
const viewportStatus = requireElement<HTMLElement>("viewport-status");
const announcer = requireElement<HTMLElement>("announcer");
const viewportCanvas = requireElement<HTMLElement>("viewport-canvas");
const stageHeading = requireElement<HTMLElement>("stage-heading");
const stageSubtitle = requireElement<HTMLElement>("stage-subtitle");
const triangleCount = requireElement<HTMLOutputElement>("triangle-count");
const actionAvailability = requireElement<HTMLOutputElement>("action-availability");
const demoGalleryList = requireElement<HTMLUListElement>("demo-gallery-list");
const demoGalleryStatus = requireElement<HTMLElement>("demo-gallery-status");
const stageSelectionChip = requireElement<HTMLElement>("stage-selection-chip");
const stageSelectionLabel = requireElement<HTMLElement>("stage-selection-label");
const selectionFlow = requireSvgElement<SVGSVGElement>("selection-flow");
const selectionFlowPath = requireSvgElement<SVGPathElement>("selection-flow-path");
const selectionFlowNode = requireSvgElement<SVGCircleElement>("selection-flow-node");
const actionButtons = Array.from(
  document.querySelectorAll<HTMLButtonElement>("[data-action]"),
);
const liveryInputs = Array.from(
  document.querySelectorAll<HTMLInputElement>('input[name="livery"]'),
);

const LIVERIES: Readonly<Record<string, GlobalLivery>> = {
  "oxide-red": defaultLivery,
  graphite: {
    paletteId: "graphite",
    primary: "#59615e",
    accent: "#c6b681",
    emission: 0.06,
  },
  field: {
    paletteId: "field",
    primary: "#747d55",
    accent: "#dfbd63",
    emission: 0.08,
  },
};
const triangleFormatter = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });

let session: ConstructorSession | null = null;
let surface: ThreeAssemblySurface | null = null;
let unsubscribeSession: (() => void) | null = null;
let unsubscribePick: (() => void) | null = null;
let handledViewRevision = -1;
let presentationGeneration = 0;
let requestedPlanKey = "";
let firstPresentationReady = false;
let disposed = false;
let selectedPointForFlow: string | null = null;
let selectionFlowFrame = 0;
let lastSelectionFlowUpdate = 0;
let activeCategory: PaletteCategory = "locomotion";
let categoryPointId: string | null = null;

const browserApi: ConstructorBrowserApi = {
  ready: false,
  status: "booting",
  getSnapshot: () => session?.snapshot() ?? null,
  debug: () => surface?.debugSnapshot() ?? null,
  selectPoint: (pointId) => dispatch({ kind: "select-point", pointId }),
  previewModule: (moduleId) => dispatch({ kind: "preview-module", moduleId }),
  previewBase: (moduleId) => dispatch({ kind: "preview-base", moduleId }),
  previewDemonstrationMech: (demonstrationMechId) => {
    const demonstrationMech = demoMechs.find(
      (candidate) => candidate.id === demonstrationMechId,
    );
    return demonstrationMech == null
      ? Promise.resolve(null)
      : dispatch({
          kind: "preview-recipe",
          recipe: demonstrationMech.recipe,
          demonstrationMechId: demonstrationMech.id,
          label: demonstrationMech.label,
        });
  },
  play: (action) => {
    const played = surface?.play(action) ?? false;
    if (surface != null) {
      const debug = surface.debugSnapshot();
      writeSurfaceDiagnostics(debug);
      syncActionControls(debug);
    }
    return played;
  },
  confirmPreview: () => {
    const tryOn = session?.snapshot().tryOn;
    return tryOn == null
      ? Promise.resolve(null)
      : dispatch({
          kind: "confirm-preview",
          previewId: tryOn.id,
          ...(tryOn.requiresRemovalAcceptance ? { acceptRemoval: true as const } : {}),
        });
  },
  cancelPreview: () => {
    const tryOn = session?.snapshot().tryOn;
    return tryOn == null
      ? Promise.resolve(null)
      : dispatch({ kind: "cancel-preview", previewId: tryOn.id });
  },
  dispose,
};
window.__MECH_CONSTRUCTOR__ = browserApi;

void initialise().catch((error: unknown) => {
  const message = errorMessage(error);
  console.error("[MECH CONSTRUCTOR]", error);
  browserApi.status = "error";
  browserApi.ready = false;
  app.dataset.state = "error";
  documentStateLabel.textContent = "Конструктор не открыт";
  viewportStatus.hidden = false;
  viewportStatus.innerHTML = "";
  viewportStatus.append(
    element("strong", {}, "Не удалось открыть экспонат"),
    element("span", {}, message),
  );
  moduleList.replaceChildren(
    element(
      "p",
      { className: "error-message" },
      `Проверьте локальный сервер и файлы GLB. ${message}`,
    ),
  );
});

async function initialise(): Promise<void> {
  const store = new LocalStorageRecipeStore("mech-constructor");
  session = await createConstructorSession({
    catalog: spikeCatalog,
    demonstrationRecipe,
    store,
  });
  surface = new ThreeAssemblySurface(viewportCanvas);
  selectionFlowFrame = requestAnimationFrame(updateSelectionFlow);
  unsubscribePick = surface.onPointPick((pointId) => {
    void dispatch({ kind: "select-point", pointId });
  });
  unsubscribeSession = session.subscribe((snapshot) => {
    if (snapshot.viewRevision === handledViewRevision) return;
    handledViewRevision = snapshot.viewRevision;
    renderSnapshot(snapshot);
    void syncPresentation(snapshot);
  });
  window.addEventListener("beforeunload", onBeforeUnload, { once: true });
}

function renderSnapshot(snapshot: ConstructorSnapshot): void {
  renderStageIdentity(snapshot);
  renderDemoGallery(snapshot);
  renderAssemblyTree(snapshot);
  renderCatalog(snapshot);
  renderTryOn(snapshot);
  renderSaved(snapshot);
  renderSelectionFlow(snapshot);
  undoButton.disabled = !snapshot.canUndo;
  redoButton.disabled = !snapshot.canRedo;
  partCount.value = `${snapshot.plan.nodes.length} ${pluralModules(snapshot.plan.nodes.length)}`;
  for (const input of liveryInputs) {
    input.checked = input.value === snapshot.displayPlan.livery.paletteId;
    input.disabled = snapshot.tryOn != null;
    input.title = snapshot.tryOn == null
      ? "Применить ливрею к текущей сборке"
      : "Сначала подтвердите или отмените примерку";
  }
}

function renderStageIdentity(snapshot: ConstructorSnapshot): void {
  const base = snapshot.displayPlan.nodes.find((node) => node.parentPositionId == null);
  const previewLabel = snapshot.tryOn?.targetKind === "recipe" ? snapshot.tryOn.label : null;
  stageHeading.textContent = previewLabel ?? base?.label ?? "Мех";
  const family = base == null ? null : modulePresentation.get(base.moduleId)?.family ?? null;
  const familyCopy = family == null ? "Мех" : familyLabel(family);
  const stateCopy = snapshot.tryOn == null ? "текущая сборка" : "примерка без сохранения";
  stageSubtitle.textContent = `${familyCopy} · ${snapshot.displayPlan.nodes.length} ${pluralModules(snapshot.displayPlan.nodes.length)} · ${stateCopy}`;
}

function renderDemoGallery(snapshot: ConstructorSnapshot): void {
  const confirmedSignature = recipeSignature(snapshot.recipe);
  const activeDemonstrationMechId = snapshot.tryOn?.targetKind === "recipe"
    ? snapshot.tryOn.demonstrationMechId ?? null
    : null;
  const focusedCard = document.activeElement instanceof HTMLElement
    ? document.activeElement.closest<HTMLButtonElement>(".demo-mech-card")
    : null;
  const focusedDemonstrationMechId = focusedCard != null && demoGalleryList.contains(focusedCard)
    ? focusedCard.dataset.demonstrationMechId ?? null
    : null;
  const cardById = new Map<string, HTMLButtonElement>();
  const cards = demoMechs.map((demonstrationMech) => {
    const installed = confirmedSignature === recipeSignature(demonstrationMech.recipe);
    const previewed = activeDemonstrationMechId === demonstrationMech.id;
    const state = previewed ? "Примерка" : installed ? "Установлен" : "Примерить";
    const image = document.createElement("img");
    image.className = "demo-mech-preview";
    image.src = demonstrationMech.thumbnailRef;
    image.alt = "";
    image.loading = "lazy";
    image.decoding = "async";
    image.width = 160;
    image.height = 100;

    const fallback = element(
      "span",
      { className: "demo-mech-fallback", ariaHidden: "true" },
      demonstrationMechInitials(demonstrationMech),
    );
    const frame = element("span", { className: "demo-mech-preview-frame" }, fallback, image);
    image.addEventListener("load", () => frame.setAttribute("data-loaded", "true"), { once: true });
    image.addEventListener("error", () => frame.setAttribute("data-error", "true"), { once: true });

    const button = element(
      "button",
      {
        className: "demo-mech-card",
        type: "button",
        title: `${demonstrationMech.description} По мотивам: ${demonstrationMech.referenceLabel}.`,
        ariaCurrent: String(installed),
        ariaPressed: String(previewed),
        dataset: { demonstrationMechId: demonstrationMech.id },
      },
      frame,
      element(
        "span",
        { className: "demo-mech-copy" },
        element("strong", {}, demonstrationMech.label),
        element("span", { className: "demo-mech-state" }, state),
      ),
    );
    const accessibleAction = previewed
      ? "Отменить примерку"
      : installed
        ? "Установлен"
        : "Примерить";
    button.setAttribute("aria-label", `${accessibleAction} «${demonstrationMech.label}»`);
    button.addEventListener("click", () => {
      if (previewed && snapshot.tryOn != null) {
        void dispatch({ kind: "cancel-preview", previewId: snapshot.tryOn.id });
        return;
      }
      if (installed && snapshot.tryOn == null) {
        announce(`«${demonstrationMech.label}» уже установлен`);
        return;
      }
      void dispatch({
        kind: "preview-recipe",
        recipe: demonstrationMech.recipe,
        demonstrationMechId: demonstrationMech.id,
        label: demonstrationMech.label,
      });
    });
    cardById.set(demonstrationMech.id, button);
    return element("li", {}, button);
  });
  demoGalleryList.setAttribute("aria-busy", "false");
  demoGalleryList.replaceChildren(...cards);
  if (focusedDemonstrationMechId != null) {
    cardById.get(focusedDemonstrationMechId)?.focus({ preventScroll: true });
  }
  demoGalleryStatus.textContent = activeDemonstrationMechId == null
    ? ""
    : `Демонстрационный мех «${snapshot.tryOn?.label ?? activeDemonstrationMechId}» показан в режиме примерки`;
}

function recipeSignature(recipe: BuildRecipe): string {
  return JSON.stringify({
    base: recipe.base.moduleId,
    placements: [...recipe.placements]
      .sort((left, right) => left.positionId.localeCompare(right.positionId))
      .map((placement) => [
        placement.positionId,
        placement.parentPositionId,
        placement.attachmentPointId,
        placement.moduleId,
      ]),
    unresolved: recipe.unresolved,
    livery: recipe.livery,
  });
}

function demonstrationMechInitials(demonstrationMech: DemoMech): string {
  return demonstrationMech.label
    .split(/[\s-]+/u)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function renderSelectionFlow(snapshot: ConstructorSnapshot): void {
  const point = selectedPoint(snapshot);
  selectedPointForFlow = point?.instanceId ?? null;
  stageSelectionChip.hidden = point == null;
  if (point == null) {
    selectionFlow.dataset.active = "false";
    return;
  }
  stageSelectionLabel.textContent = point.label;
  selectionFlow.dataset.active = "true";
}

function updateSelectionFlow(time: number): void {
  if (disposed) return;
  selectionFlowFrame = requestAnimationFrame(updateSelectionFlow);
  if (time - lastSelectionFlowUpdate < 72) return;
  lastSelectionFlowUpdate = time;
  if (selectedPointForFlow == null || window.matchMedia("(max-width: 820px)").matches) return;
  const source = assemblyTree.querySelector<HTMLElement>('.tree-point[aria-current="true"]');
  const projected = surface?.projectPointClient(selectedPointForFlow) ?? null;
  if (source == null || projected == null) return;

  const workspaceRect = constructorWorkspace.getBoundingClientRect();
  const sourceRect = source.getBoundingClientRect();
  const destinationRect = selectionRecord.getBoundingClientRect();
  const width = Math.max(constructorWorkspace.clientWidth, 1);
  const height = Math.max(constructorWorkspace.clientHeight, 1);
  const sourceX = sourceRect.right - workspaceRect.left;
  const sourceY = sourceRect.top + sourceRect.height * 0.5 - workspaceRect.top;
  const pointX = projected[0] - workspaceRect.left;
  const pointY = projected[1] - workspaceRect.top;
  const destinationX = destinationRect.left - workspaceRect.left;
  const destinationY = destinationRect.top + Math.min(destinationRect.height * 0.5, 28) - workspaceRect.top;
  const sourceControl = Math.min(sourceX + 74, pointX - 22);
  const destinationControl = Math.max(destinationX - 74, pointX + 22);

  selectionFlow.setAttribute("viewBox", `0 0 ${width} ${height}`);
  selectionFlowPath.setAttribute(
    "d",
    `M ${sourceX} ${sourceY} C ${sourceControl} ${sourceY}, ${pointX - 48} ${pointY}, ${pointX} ${pointY} ` +
      `C ${pointX + 48} ${pointY}, ${destinationControl} ${destinationY}, ${destinationX} ${destinationY}`,
  );
  selectionFlowNode.setAttribute("cx", String(pointX));
  selectionFlowNode.setAttribute("cy", String(pointY));
}

async function syncPresentation(snapshot: ConstructorSnapshot): Promise<void> {
  const currentSurface = surface;
  if (currentSurface == null || disposed) return;
  const generation = ++presentationGeneration;
  const mode = snapshot.tryOn == null
    ? "confirmed"
    : snapshot.tryOn.targetKind === "recipe"
      ? "recipe-try-on"
      : "try-on";
  const planKey = JSON.stringify({
    mode,
    nodes: snapshot.displayPlan.nodes.map((node) => [
      node.positionId,
      node.moduleId,
      node.parentPositionId,
      node.attachmentPointId,
      node.attachmentAnchor,
    ]),
    livery: snapshot.displayPlan.livery,
  });

  try {
    if (planKey !== requestedPlanKey) {
      requestedPlanKey = planKey;
      await currentSurface.apply(snapshot.displayPlan, mode);
    }
    if (generation !== presentationGeneration || disposed) return;
    currentSurface.selectPoint(selectedPoint(snapshot));
    const debug = currentSurface.debugSnapshot();
    writeSurfaceDiagnostics(debug);
    syncActionControls(debug);
    triangleCount.value = triangleFormatter.format(debug.triangles);
    if (!firstPresentationReady) {
      firstPresentationReady = true;
      viewportStatus.hidden = true;
      app.dataset.state = "ready";
      browserApi.ready = true;
      browserApi.status = "ready";
      setDocumentState("Текущая сборка хранится локально", "ready");
    }
  } catch (error: unknown) {
    requestedPlanKey = "";
    if (generation !== presentationGeneration || disposed) return;
    const message = errorMessage(error);
    console.error("[ASSEMBLY SURFACE]", error);
    viewportStatus.hidden = false;
    viewportStatus.replaceChildren(
      element("strong", {}, "3D-сборка не готова"),
      element("span", {}, message),
    );
    setDocumentState("Ошибка отображения — рецепт сохранён", "error");
    announce(`Ошибка отображения: ${message}`);
  }
}

function renderAssemblyTree(snapshot: ConstructorSnapshot): void {
  treeLoading.hidden = true;
  const nodeById = new Map(snapshot.plan.nodes.map((node) => [node.positionId, node]));
  const items = snapshot.plan.points.map((point) => {
    const owner = nodeById.get(point.parentPositionId);
    const occupied = point.occupiedPositionId == null ? null : nodeById.get(point.occupiedPositionId);
    const depth = owner == null ? 0 : nodeDepth(owner.positionId, nodeById);
    const button = element("button", {
      className: "tree-point",
      type: "button",
      dataset: {
        pointId: point.instanceId,
        occupied: String(occupied != null),
      },
      ariaCurrent: String(point.instanceId === snapshot.selectedPointId),
    });
    button.append(
      element("span", { className: "point-state", ariaHidden: "true" }),
      element(
        "span",
        { className: "point-copy" },
        element("strong", {}, point.label),
        element("small", {}, occupied?.label ?? "Свободное крепление"),
      ),
      element("span", { className: "point-code tabular" }, shortPointCode(point.pointId)),
    );
    button.addEventListener("click", () => {
      void dispatch({ kind: "select-point", pointId: point.instanceId });
    });
    return element("li", { className: `tree-branch depth-${Math.min(depth, 2)}` }, button);
  });
  assemblyTree.replaceChildren(...items);
}

function renderCatalog(snapshot: ConstructorSnapshot): void {
  const point = selectedPoint(snapshot);
  if (categoryPointId !== snapshot.selectedPointId) {
    categoryPointId = snapshot.selectedPointId;
    activeCategory = point == null
      ? activeCategory === "compatible" ? "locomotion" : activeCategory
      : "compatible";
  }
  catalogDrawer.dataset.selected = String(point != null);
  moduleList.setAttribute("aria-busy", "false");
  drawerCode.textContent = `${spikeCatalog.modules.length} МОДУЛЕЙ`;

  if (point == null) {
    selectionTitle.textContent = "Весь каталог открыт";
    selectionDetail.textContent = "Выберите ходовую часть или откройте категорию.";
  } else {
    const occupied = point.occupiedPositionId == null
      ? null
      : snapshot.plan.nodes.find((node) => node.positionId === point.occupiedPositionId);
    selectionTitle.textContent = point.label;
    selectionDetail.textContent = occupied == null
      ? `Свободно · принимает: ${point.accepts.map(connectionLabel).join(", ")}`
      : `Установлено: ${occupied.label}`;
  }

  renderCatalogIndex(snapshot);
  const compatibleIds = new Set(snapshot.compatibleModules.map((choice) => choice.moduleId));
  const displayed = spikeCatalog.modules.filter((module) => {
    if (activeCategory === "compatible") return compatibleIds.has(module.id);
    return modulePresentation.get(module.id)?.category === activeCategory;
  });
  catalogStatus.textContent = activeCategory === "compatible"
    ? `${displayed.length} ${compatibleWord(displayed.length)} ${pluralModules(displayed.length)}`
    : `${categoryLabels[activeCategory]} · ${displayed.length} ${pluralModules(displayed.length)}`;

  const choices: HTMLElement[] = [];
  const occupied = point?.occupiedPositionId == null
    ? null
    : snapshot.plan.nodes.find((node) => node.positionId === point.occupiedPositionId);
  const occupiedCategory = occupied == null
    ? null
    : modulePresentation.get(occupied.moduleId)?.category ?? null;
  if (
    occupied != null &&
    (activeCategory === "compatible" || activeCategory === occupiedCategory)
  ) {
    choices.push(renderRemoveChoice(snapshot, occupied.label));
  }
  choices.push(...displayed.map((module) => renderModuleCard(snapshot, module, compatibleIds)));
  if (choices.length === 0) {
    choices.push(
      element(
        "p",
        { className: "empty-register" },
        point == null
          ? "В этой категории пока нет доступных модулей."
          : "Для выбранной точки нет совместимых модулей.",
      ),
    );
  }
  moduleList.replaceChildren(...choices);
}

function renderCatalogIndex(snapshot: ConstructorSnapshot): void {
  const categories: readonly PaletteCategory[] = [
    "compatible",
    "locomotion",
    "cockpit",
    "weapon",
    "backpack",
    "shoulder",
    "equipment",
  ];
  const pointSelected = snapshot.selectedPointId != null;
  const buttons = categories.map((category) => {
    const count = category === "compatible"
      ? snapshot.compatibleModules.length
      : spikeCatalog.modules.filter(
          (module) => modulePresentation.get(module.id)?.category === category,
        ).length;
    const label = category === "compatible" ? "Подходят" : categoryLabels[category];
    const button = element(
      "button",
      {
        className: "catalog-category",
        type: "button",
        ariaPressed: String(activeCategory === category),
        dataset: { category },
      },
      element("span", {}, label),
      element("span", { className: "tabular" }, String(count)),
    );
    if (category === "compatible" && !pointSelected) {
      button.disabled = true;
      button.title = "Сначала выберите точку крепления на мехе";
    }
    button.addEventListener("click", () => {
      activeCategory = category;
      const current = session?.snapshot();
      if (current != null) renderCatalog(current);
    });
    return button;
  });
  catalogIndex.replaceChildren(...buttons);
}

function renderModuleCard(
  snapshot: ConstructorSnapshot,
  module: CatalogModule,
  compatibleIds: ReadonlySet<string>,
): HTMLElement {
  const presentation = modulePresentation.get(module.id);
  if (presentation == null) throw new Error(`Presentation is missing for ${module.id}`);
  const currentBase = snapshot.recipe.base.moduleId === module.id;
  const point = selectedPoint(snapshot);
  const installedHere = point?.occupiedPositionId == null
    ? false
    : snapshot.plan.nodes.some(
        (node) => node.positionId === point.occupiedPositionId && node.moduleId === module.id,
      );
  const installedElsewhere = snapshot.plan.nodes.some((node) => node.moduleId === module.id);
  const previewed = snapshot.tryOn?.targetKind !== "recipe" && snapshot.tryOn?.moduleId === module.id;
  const compatible = compatibleIds.has(module.id);
  const candidate = module.role === "module" ? candidatePoint(snapshot, module) : null;
  const fit = currentBase || installedHere
    ? "installed"
    : previewed
      ? "preview"
      : compatible
        ? "compatible"
        : candidate != null
          ? "other-point"
          : "unavailable";
  const status = currentBase || installedHere
    ? "Установлено"
    : previewed
      ? "Примерка"
      : compatible
        ? "Подходит"
        : candidate != null
          ? `Точка: ${candidate.label}`
          : installedElsewhere
            ? "Есть в сборке"
            : module.role === "base"
              ? "Сменить ходовую"
              : `Нужен разъём: ${connectionLabel(module.connectionType ?? "")}`;
  const index = spikeCatalog.modules.findIndex((candidateModule) => candidateModule.id === module.id);
  const preview = document.createElement("img");
  preview.className = "module-preview";
  preview.src = presentation.previewRef;
  preview.alt = "";
  preview.loading = "lazy";
  preview.decoding = "async";
  preview.width = 128;
  preview.height = 128;

  const button = element("button", {
    className: "module-choice module-card",
    type: "button",
    ariaCurrent: String(currentBase || installedHere),
    ariaPressed: String(previewed),
    title: currentBase || installedHere ? "Этот модуль уже установлен" : `Примерить: ${module.label}`,
    dataset: { fit, category: presentation.category },
  });
  button.append(
    element("span", { className: "module-preview-frame" }, preview),
    element(
      "span",
      { className: "module-copy" },
      element("strong", {}, module.label),
      element("small", {}, presentation.sourceId),
      element(
        "span",
        { className: "module-provenance" },
        `${familyLabel(presentation.family)} · M${String(index + 1).padStart(2, "0")}`,
      ),
    ),
    element("span", { className: "fit-label" }, status),
  );
  button.addEventListener("click", () => {
    if ((currentBase || installedHere) && snapshot.tryOn == null) {
      announce("Этот модуль уже установлен");
      return;
    }
    if (module.role === "base") {
      void dispatch({ kind: "preview-base", moduleId: module.id });
      return;
    }
    if (compatible) {
      void dispatch({ kind: "preview-module", moduleId: module.id });
      return;
    }
    if (candidate == null) {
      announce(`Сначала установите модуль с разъёмом «${connectionLabel(module.connectionType ?? "")}»`);
      return;
    }
    void (async () => {
      const selected = await dispatch({ kind: "select-point", pointId: candidate.instanceId });
      if (selected?.ok) await dispatch({ kind: "preview-module", moduleId: module.id });
    })();
  });
  return button;
}

function renderRemoveChoice(snapshot: ConstructorSnapshot, installedLabel: string): HTMLElement {
  const remove = element("button", {
    className: "module-choice remove-choice",
    type: "button",
    ariaPressed: String(
      snapshot.tryOn?.targetKind === "attachment-point" && snapshot.tryOn.moduleId === null,
    ),
  });
  remove.append(
    element("span", { className: "module-index tabular" }, "Ø"),
    element(
      "span",
      { className: "module-copy" },
      element("strong", {}, "Освободить точку"),
      element("small", {}, `Снять: ${installedLabel}`),
    ),
    element("span", { className: "fit-label" }, "Обратимо"),
  );
  remove.addEventListener("click", () => {
    void dispatch({ kind: "preview-module", moduleId: null });
  });
  return remove;
}

function candidatePoint(
  snapshot: ConstructorSnapshot,
  module: CatalogModule,
): AttachmentPointView | null {
  const connectionType = module.connectionType;
  if (connectionType == null) return null;
  const candidates = snapshot.plan.points.filter((point) => point.accepts.includes(connectionType));
  return candidates.find((point) => point.occupiedPositionId == null) ?? candidates[0] ?? null;
}

function renderTryOn(snapshot: ConstructorSnapshot): void {
  const tryOn = snapshot.tryOn;
  if (tryOn == null) {
    tryOnPanel.hidden = true;
    return;
  }
  tryOnPanel.hidden = false;
  if (tryOn.targetKind === "recipe") {
    const replacedCount = tryOn.consequences.reduce(
      (total, consequence) => total + consequence.moduleCount,
      0,
    );
    tryOnSummary.textContent = `«${tryOn.label ?? "Готовый мех"}» показан целиком. Текущая сборка не изменится до подтверждения.`;
    tryOnConsequences.replaceChildren(
      element(
        "li",
        {},
        `Будет явно заменена текущая сборка: ${replacedCount} ${pluralModules(replacedCount)}`,
      ),
      element("li", {}, "Предыдущее состояние останется доступно через действие «Отменить»"),
    );
    confirmButton.textContent = `Подтвердить замену · ${replacedCount}`;
    return;
  }
  const moduleLabel = tryOn.moduleId == null
    ? "модуль будет снят"
    : snapshot.displayPlan.nodes.find((node) => node.moduleId === tryOn.moduleId)?.label ??
      tryOn.moduleId;
  tryOnSummary.textContent = tryOn.moduleId == null
    ? "Точка станет свободной. Текущая сборка не изменится до подтверждения."
    : tryOn.targetKind === "base"
      ? `${moduleLabel} показана как новая ходовая часть. Сборка пока не изменена.`
      : `${moduleLabel} показана на мехе. Текущая сборка пока не изменена.`;

  if (tryOn.consequences.length === 0) {
    tryOnConsequences.replaceChildren(
      element("li", {}, "Дочерние модули не будут потеряны"),
    );
    confirmButton.textContent = "Подтвердить";
  } else {
    const removedCount = tryOn.consequences.reduce(
      (total, consequence) => total + consequence.moduleCount,
      0,
    );
    tryOnConsequences.replaceChildren(
      ...tryOn.consequences.map((consequence) => {
        const rootLabel = snapshot.plan.nodes.find(
          (node) => node.positionId === consequence.rootPositionId,
        )?.label;
        return element(
          "li",
          {},
          rootLabel == null
            ? `Будет снята ветвь: ${consequence.moduleCount} ${pluralModules(consequence.moduleCount)}`
            : `Будет снято: ${rootLabel}`,
        );
      }),
    );
    confirmButton.textContent = `Подтвердить снятие · ${removedCount}`;
  }
}

function renderSaved(snapshot: ConstructorSnapshot): void {
  savedCount.textContent = String(snapshot.savedMechs.length);
  if (snapshot.savedMechs.length === 0) {
    savedList.replaceChildren(
      element("li", { className: "empty-register" }, "Именованных версий пока нет"),
    );
    return;
  }
  savedList.replaceChildren(
    ...snapshot.savedMechs.map((saved) => {
      const open = element("button", { type: "button" }, saved.name);
      open.addEventListener("click", () => {
        void dispatch({ kind: "load-named", id: saved.id });
      });
      return element(
        "li",
        { className: "saved-item" },
        open,
        element("time", { className: "tabular" }, saved.id.toUpperCase()),
      );
    }),
  );
}

async function dispatch(
  intent: Parameters<ConstructorSession["dispatch"]>[0],
): Promise<ActionOutcome | null> {
  if (session == null || disposed) return null;
  const outcome = await session.dispatch(intent);
  if (!outcome.ok) {
    setDocumentState(russianError(outcome.code), "error");
    announce(russianError(outcome.code));
    return outcome;
  }
  const message = outcomeMessage(outcome.effect);
  if (message != null) {
    setDocumentState(message, "ready");
    announce(message);
  }
  return outcome;
}

confirmButton.addEventListener("click", () => {
  const tryOn = session?.snapshot().tryOn;
  if (tryOn == null) return;
  void dispatch({
    kind: "confirm-preview",
    previewId: tryOn.id,
    ...(tryOn.requiresRemovalAcceptance ? { acceptRemoval: true as const } : {}),
  });
});

cancelButton.addEventListener("click", () => {
  const tryOn = session?.snapshot().tryOn;
  if (tryOn == null) return;
  void dispatch({ kind: "cancel-preview", previewId: tryOn.id });
});

undoButton.addEventListener("click", () => void dispatch({ kind: "undo" }));
redoButton.addEventListener("click", () => void dispatch({ kind: "redo" }));

saveForm.addEventListener("submit", (event) => {
  event.preventDefault();
  void dispatch({ kind: "save-named", name: saveName.value }).then((outcome) => {
    if (outcome?.ok && outcome.effect === "saved") saveName.value = "";
  });
});

for (const button of actionButtons) {
  button.addEventListener("click", () => {
    const action = button.dataset.action as DemonstrationAction;
    const played = surface?.play(action) ?? false;
    if (surface != null) {
      const debug = surface.debugSnapshot();
      writeSurfaceDiagnostics(debug);
      syncActionControls(debug);
    }
    if (!played) {
      announce(`Действие «${button.textContent?.trim() ?? action}» недоступно для этой базы`);
      return;
    }
    announce(`Действие: ${button.textContent?.trim() ?? action}`);
  });
}

for (const input of liveryInputs) {
  input.addEventListener("change", () => {
    if (!input.checked) return;
    const livery = LIVERIES[input.value];
    if (livery != null) void dispatch({ kind: "set-livery", livery });
  });
}

async function dispose(): Promise<void> {
  if (disposed) return;
  disposed = true;
  cancelAnimationFrame(selectionFlowFrame);
  browserApi.ready = false;
  browserApi.status = "disposed";
  unsubscribePick?.();
  unsubscribeSession?.();
  unsubscribePick = null;
  unsubscribeSession = null;
  await Promise.all([session?.dispose(), surface?.dispose()]);
  session = null;
  surface = null;
  app.dataset.state = "disposed";
  documentStateLabel.textContent = "Конструктор закрыт";
}

function onBeforeUnload(): void {
  void dispose();
}

function selectedPoint(snapshot: ConstructorSnapshot): AttachmentPointView | null {
  return (
    snapshot.plan.points.find((point) => point.instanceId === snapshot.selectedPointId) ?? null
  );
}

function nodeDepth(
  positionId: string,
  nodes: ReadonlyMap<string, ConstructorSnapshot["plan"]["nodes"][number]>,
): number {
  let depth = 0;
  let cursor = nodes.get(positionId);
  while (cursor?.parentPositionId != null) {
    depth += 1;
    cursor = nodes.get(cursor.parentPositionId);
  }
  return depth;
}

function shortPointCode(pointId: string): string {
  return pointId
    .split("-")
    .map((segment) => segment[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 3);
}

function connectionLabel(connectionType: string): string {
  const labels: Readonly<Record<string, string>> = {
    cockpit: "кабина",
    weapon: "оружие",
    backpack: "ранец",
    antenna: "антенна",
    "shoulder-frame": "плечевая рама",
    "half-shoulder": "плечо или щит",
  };
  return labels[connectionType] ?? connectionType;
}

function familyLabel(family: ModulePresentation["family"]): string {
  const labels = {
    spider: "Паук",
    tracks: "Гусеницы",
    tank: "Танк",
    buggy: "Багги",
    universal: "Универсальная",
  } as const;
  return labels[family];
}

function setDocumentState(message: string, state: "ready" | "error"): void {
  documentStateLabel.textContent = message;
  app.dataset.state = state;
}

function writeSurfaceDiagnostics(debug: ReturnType<ThreeAssemblySurface["debugSnapshot"]>): void {
  viewportCanvas.dataset.ready = String(debug.ready);
  viewportCanvas.dataset.nodeCount = String(debug.nodeCount);
  viewportCanvas.dataset.mode = debug.mode;
  viewportCanvas.dataset.activeAction = debug.activeAction;
  viewportCanvas.dataset.availableActions = debug.availableActions.join(",");
  viewportCanvas.dataset.triangles = String(debug.triangles);
  viewportCanvas.dataset.cameraPosition = JSON.stringify(debug.cameraPosition);
  viewportCanvas.dataset.assemblySize = JSON.stringify(debug.assemblySize);
  viewportCanvas.dataset.rendererMemory = JSON.stringify(debug.renderer);
}

function syncActionControls(
  debug: ReturnType<ThreeAssemblySurface["debugSnapshot"]>,
): void {
  const available = new Set<DemonstrationAction>(debug.availableActions);
  for (const button of actionButtons) {
    const action = button.dataset.action as DemonstrationAction;
    const enabled = available.has(action);
    button.disabled = !enabled;
    button.setAttribute("aria-pressed", String(debug.activeAction === action));
    button.title = enabled
      ? `Показать действие: ${button.textContent?.trim() ?? action}`
      : `У выбранной базы нет совместимого клипа «${button.textContent?.trim() ?? action}»`;
  }
  const authoredCount = debug.availableActions.filter((action) => action !== "rest").length;
  actionAvailability.value = authoredCount === 0
    ? "только стойка"
    : authoredCount === 1
      ? "1 авторская анимация"
      : `${authoredCount} авторские анимации`;
}

function announce(message: string): void {
  announcer.textContent = "";
  requestAnimationFrame(() => {
    announcer.textContent = message;
  });
}

function outcomeMessage(effect: SuccessfulOutcome["effect"]): string | null {
  switch (effect) {
    case "previewed":
      return "Примерка готова — подтвердите или отмените";
    case "cancelled":
      return "Примерка отменена";
    case "committed":
      return "Текущая сборка подтверждена и сохранена";
    case "undone":
      return "Последнее изменение отменено";
    case "redone":
      return "Изменение повторено";
    case "saved":
      return "Именованный мех сохранён локально";
    case "loaded":
      return "Сохранённый мех открыт";
    case "selected":
    case "no-change":
      return null;
  }
  return null;
}

function russianError(code: RejectedOutcome["code"]): string {
  const messages: Record<string, string> = {
    ATTACHMENT_POINT_UNKNOWN: "Сначала выберите доступную точку крепления",
    MODULE_UNKNOWN: "Такого модуля больше нет в каталоге",
    WRONG_MODULE_ROLE: "Базу нельзя установить как дочерний модуль",
    CONNECTION_TYPE_MISMATCH: "Тип соединения этого модуля не подходит",
    EXPLICITLY_DENIED: "Эта точная комбинация запрещена каталогом",
    TRY_ON_MISSING: "Активной примерки уже нет",
    TRY_ON_STALE: "Эта примерка устарела — выберите модуль снова",
    REMOVAL_ACCEPTANCE_REQUIRED: "Сначала подтвердите показанное снятие дочерних модулей",
    NOTHING_TO_UNDO: "Отменять больше нечего",
    NOTHING_TO_REDO: "Повторять больше нечего",
    SAVE_NAME_REQUIRED: "Введите имя сохраняемого меха",
    SAVED_MECH_NOT_FOUND: "Эта сохранённая версия не найдена",
    STORAGE_READ_FAILED: "Локальное сохранение не удалось прочитать",
    STORAGE_WRITE_FAILED: "Изменение не применено: локальное сохранение недоступно",
    DISPOSED: "Конструктор уже закрыт",
  };
  return messages[code] ?? "Операция не выполнена";
}

function pluralModules(count: number): string {
  const lastTwo = count % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return "модулей";
  const last = count % 10;
  if (last === 1) return "модуль";
  if (last >= 2 && last <= 4) return "модуля";
  return "модулей";
}

function compatibleWord(count: number): string {
  const lastTwo = count % 100;
  return count % 10 === 1 && !(lastTwo >= 11 && lastTwo <= 14)
    ? "совместимый"
    : "совместимых";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function requireElement<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (found == null) throw new Error(`Required element is missing: #${id}`);
  return found as T;
}

function requireSvgElement<T extends SVGElement>(id: string): T {
  const found = document.getElementById(id);
  if (found == null) throw new Error(`Required SVG element is missing: #${id}`);
  return found as unknown as T;
}

function requireClosest<T extends Element>(element: Element, selector: string): T {
  const found = element.closest<T>(selector);
  if (found == null) throw new Error(`Required ancestor is missing: ${selector}`);
  return found;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attributes: {
    className?: string;
    type?: string;
    title?: string;
    ariaHidden?: string;
    ariaCurrent?: string;
    ariaPressed?: string;
    dataset?: Readonly<Record<string, string>>;
  },
  ...children: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attributes.className != null) node.className = attributes.className;
  if (attributes.type != null && "type" in node) (node as HTMLButtonElement).type = attributes.type as "button";
  if (attributes.title != null) node.title = attributes.title;
  if (attributes.ariaHidden != null) node.setAttribute("aria-hidden", attributes.ariaHidden);
  if (attributes.ariaCurrent != null) node.setAttribute("aria-current", attributes.ariaCurrent);
  if (attributes.ariaPressed != null) node.setAttribute("aria-pressed", attributes.ariaPressed);
  for (const [key, value] of Object.entries(attributes.dataset ?? {})) node.dataset[key] = value;
  node.append(...children);
  return node;
}
